import React from 'react';
import { EditableColumnType } from 'components/CustomTable/CustomTable';
import { DataType } from 'store/ducks/DataEntry/Branch/types';
import { PlusOutlined } from '@ant-design/icons';
import { Button, DatePicker } from 'antd';
import dayjs from 'dayjs';
import CustomSelect from 'components/CustomSelect';
import { IRiskExposure } from 'store/ducks/DataManagement/Article8AndICTSystem/types';
import * as Styled from '../Tables/DataEntry/DataEntry.styled';

export const getRiskExposureColumns = (
  handleDateChange: (key: number, field: string, value: string) => void,
  handleSave: (record: IRiskExposure) => void,
  handleAddRow: (record: { key: React.Key }, index: number) => void,
  handleRemoveRow: (key: number) => void,
  handleInactivate: (record: IRiskExposure) => void,
  dependencyTypes: { value: string; label: string }[],
  exposureCategories: { value: string; label: string }[],
  t: (key: string) => string,
): EditableColumnType[] => [
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('RISK_EXPOSURE.NAME')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'name',
    editable: true,
    fixed: 'left',
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('RISK_EXPOSURE.DEPENDENCY_TYPE')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'dependency_type_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={dependencyTypes}
        value={record.dependency_type_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            dependency_type_id: value,
          } as IRiskExposure)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('RISK_EXPOSURE.EXPOSURE_CATEGORY')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'exposure_category_id',
    editable: false,
    render: (_, record) => (
      <CustomSelect
        options={exposureCategories}
        value={record.exposure_category_id}
        onChange={(value: any) =>
          handleSave({
            ...record,
            exposure_category_id: value,
          } as IRiskExposure)
        }
        filterOption={(input: string, option: { label: string }) =>
          option?.label?.toLowerCase().includes(input.toLowerCase())
        }
      />
    ),
  },
  {
    title: <>{t('RISK_EXPOSURE.VALID_FROM_TO')}</>,
    dataIndex: 'date_range',
    editable: false,
    render: (_: any, record: DataType) => {
      return (
        <Styled.DateInputs>
          <DatePicker
            value={record.start_date ? dayjs(record.start_date) : null}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '1900-01-01';

              handleDateChange(record.key, 'start_date', formattedDateString);
            }}
          />
          <DatePicker
            value={record.end_date ? dayjs(record.end_date) : null}
            format="YYYY-MM-DD"
            onClick={(e) => e.stopPropagation()}
            onChange={(date) => {
              const formattedDateString = date
                ? dayjs(date).format('YYYY-MM-DD')
                : '9999-01-01';

              handleDateChange(record.key, 'end_date', formattedDateString);
            }}
          />
        </Styled.DateInputs>
      );
    },
  },
  {
    title: (
      <Styled.TitleComponent>
        <div>
          <p>{t('RISK_EXPOSURE.DESCRIPTION')}</p>
        </div>
      </Styled.TitleComponent>
    ),
    dataIndex: 'description',
    editable: true,
  },
  {
    title: t('GENERAL.ACTIONS'),
    key: 'actions',
    width: 150,
    render: (_: any, record) => (
      <>
        {record.isNew ? (
          <Button danger onClick={() => handleRemoveRow(record.key)}>
            {t('GENERAL.REMOVE')}
          </Button>
        ) : (
          <Button
            danger
            onClick={() => handleInactivate(record as IRiskExposure)}
          >
            {t('GENERAL.SOFT_DELETE')}
          </Button>
        )}
      </>
    ),
  },
];
